// Pure presentational helpers for the Strategies tab. No @react-spectrum/S2 import
// so the vitest suite can run offline.

import type {
	FunnelStage,
	MetricBasis,
	MetricTracking,
	StrategyMetric,
	WhereLocus,
	StrategicDirection,
	StrategyCampaignProposal,
	StrategyDefinition,
} from '../../../../bridge/types/strategy.ts';

// --- Creativity bands (owner-facing vocabulary) ---------------------------
// Fixed levels a single Generate fans out into (0/50/100), and the floors that bucket
// a stored creativity_level into a band. Must match backend shared/strategy/creativity.py.
export const CREATIVITY_BATCH_LEVELS = [0, 50, 100] as const;
const CREATIVITY_BOLD_FLOOR = 34;
const CREATIVITY_STUNT_FLOOR = 67;

export type CreativityBand = 'mid' | 'bold' | 'stunt';
export type TierIcon = 'proven' | 'bold' | 'swing';

export function creativityBand(
	level: number | null | undefined,
): CreativityBand {
	const v = typeof level === 'number' && Number.isFinite(level) ? level : 0;
	if (v >= CREATIVITY_STUNT_FLOOR) return 'stunt';
	if (v >= CREATIVITY_BOLD_FLOOR) return 'bold';
	return 'mid';
}

// Plain-language, owner-facing name for a band (internal codenames stay in code).
export function creativityBandLabel(band: CreativityBand): string {
	switch (band) {
		case 'stunt':
			return 'Big swing';
		case 'bold':
			return 'Bold';
		case 'mid':
			return 'Proven';
	}
}

// One-line owner-facing hint under a band label — the kind of move, never a metric or boast.
export function creativityBandHint(band: CreativityBand): string {
	switch (band) {
		case 'stunt':
			return 'the boldest, most attention-earning move';
		case 'bold':
			return 'a bold, runnable, attention-earning move';
		case 'mid':
			return 'the fastest, lowest-risk win from what you already have';
	}
}

export interface TierVisual {
	icon: TierIcon;
	// Ambition eyebrow above the label — the kind of bet, never a rank (no tier reads "worse").
	tagline: string;
}

export function tierVisual(band: CreativityBand): TierVisual {
	switch (band) {
		case 'mid':
			return { icon: 'proven', tagline: 'Lowest risk' };
		case 'bold':
			return { icon: 'bold', tagline: 'Bigger move' };
		case 'stunt':
			return { icon: 'swing', tagline: 'Boldest bet' };
	}
}

// The ink a tier is written in — one aurora-blend hue per tier, so a tier reads as one colour
// wherever it appears. This is the TIER's colour, not the strategy's: a strategy's orb wears its
// own identity hue, which is a separate system (see orbIdentity.ts). Returns the token, never a
// literal hue: the AA-safe channels live in index.css, and `tier-pill` derives label, tint, and
// hairline from them at three alphas.
export function tierInk(band: CreativityBand): string {
	switch (band) {
		case 'mid':
			return 'var(--tier-proven)';
		case 'bold':
			return 'var(--tier-bold)';
		case 'stunt':
			return 'var(--tier-swing)';
	}
}

// --- Time / duration ------------------------------------------------------

// Relative "time ago" for a timestamp; falls back to a short absolute date past a week.
export function timeAgo(iso: string, now: Date = new Date()): string {
	const then = new Date(iso).getTime();
	if (!Number.isFinite(then)) return '';
	const diffMs = now.getTime() - then;
	const sec = Math.floor(diffMs / 1000);

	if (sec < 45) return 'just now'; // clock skew / future → don't show "-3m"
	const min = Math.floor(sec / 60);
	if (min < 60) return `${min}m ago`;
	const hr = Math.floor(min / 60);
	if (hr < 24) return `${hr}h ago`;
	const day = Math.floor(hr / 24);
	if (day < 7) return `${day}d ago`;

	const d = new Date(then);
	const sameYear = d.getFullYear() === now.getFullYear();
	return d.toLocaleDateString(undefined, {
		month: 'short',
		day: 'numeric',
		...(sameYear ? {} : { year: 'numeric' }),
	});
}

// Fine-grained elapsed duration ("45s" / "2m 14s" / "1h 02m") for the run view.
// NaN/Infinity guard first so a bad startedAt never leaks "NaNs".
export function formatDuration(ms: number): string {
	if (!Number.isFinite(ms)) return '';
	const totalSec = Math.floor(Math.max(0, ms) / 1000);
	const h = Math.floor(totalSec / 3600);
	const m = Math.floor(totalSec / 60) % 60;
	const s = totalSec % 60;
	if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
	if (m > 0) return `${m}m ${s}s`;
	return `${s}s`;
}

// Minute-granular duration ("<1m" / "2m") for the list, where a per-second counter would churn.
export function formatDurationCoarse(ms: number): string {
	if (!Number.isFinite(ms)) return '';
	const totalMin = Math.floor(Math.max(0, ms) / 60000);
	if (totalMin < 1) return '<1m';
	const h = Math.floor(totalMin / 60);
	const m = totalMin % 60;
	if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
	return `${totalMin}m`;
}

// --- Metrics --------------------------------------------------------------

// Compose a metric's numeric readout: localized target joined to its unit. A "%"
// binds tight ("3%"); a count noun keeps the space ("18 sessions").
export function formatMetricValue(target: number, unit: string): string {
	const n = Number.isFinite(target) ? target.toLocaleString() : String(target);
	const u = (unit ?? '').trim();
	if (!u) return n;
	if (u === '%') return `${n}%`;
	return `${n} ${u}`;
}

// The owner's own goal, in their words, for a strategy that has no title yet. Mirrors the
// backend's OUTCOME_KEYS vocabulary (services/py/shared/strategy/constants.py) as singular/plural
// count nouns. `revenue` carries no unit: no currency is modeled on StrategyGoal, so a "$"
// would state a fact we do not have — the same defect this whole state exists to remove.
const GOAL_NOUN: Record<string, { one: string; many: string }> = {
	sales: { one: 'sale', many: 'sales' },
	bookings: { one: 'booking', many: 'bookings' },
	customers: { one: 'customer', many: 'customers' },
	leads: { one: 'lead', many: 'leads' },
	signups: { one: 'signup', many: 'signups' },
	subscribers: { one: 'subscriber', many: 'subscribers' },
	followers: { one: 'follower', many: 'followers' },
};

// "Working toward 200 bookings" — or null, which is the caller's signal that we know nothing
// true to say. `target` is typed `unknown` on purpose: the run doc is upserted whole with no
// schema validation, so a string target can persist, and a coercing guard would render it.
export function generatingGoalLabel(
	outcome: string | null | undefined,
	target: unknown,
): string | null {
	if (!outcome || typeof target !== 'number' || !Number.isFinite(target)) {
		return null;
	}
	if (target <= 0) return null;
	if (outcome === 'revenue') {
		return `Working toward a revenue goal of ${formatMetricValue(target, '')}`;
	}
	const noun = GOAL_NOUN[outcome];
	if (!noun) return null;
	return `Working toward ${formatMetricValue(target, target === 1 ? noun.one : noun.many)}`;
}

// Static starting point for the gauge (NOT live progress): how far a real baseline
// (0<baseline<target) has come toward the goal. `pct` is finite, clamped to [0,100].
export function metricGoalProgress(
	target: number,
	baseline?: number,
): { pct: number; hasStart: boolean } {
	const hasStart =
		typeof baseline === 'number' &&
		Number.isFinite(baseline) &&
		Number.isFinite(target) &&
		baseline > 0 &&
		baseline < target;
	if (!hasStart) return { pct: 0, hasStart: false };
	const pct = Math.max(0, Math.min(100, (baseline! / target) * 100));
	return { pct, hasStart: true };
}

// Evenly-spaced interior waypoints (default quartiles) between baseline and target;
// `reached` once a real baseline passes a value. Degrades for tiny spans (target 2 → [1], 1 → []).
export function metricCheckpoints(
	target: number,
	baseline?: number,
	count = 3,
): { value: number; reached: boolean }[] {
	if (
		!Number.isFinite(target) ||
		target <= 0 ||
		!Number.isInteger(count) ||
		count < 1
	) {
		return [];
	}
	const hasStart =
		typeof baseline === 'number' &&
		Number.isFinite(baseline) &&
		baseline > 0 &&
		baseline < target;
	const base = hasStart ? baseline! : 0;
	const span = target - base;
	if (span <= 0) return [];
	const seen = new Set<number>();
	const out: { value: number; reached: boolean }[] = [];
	for (let i = 1; i <= count; i++) {
		const value = Math.round(base + span * (i / (count + 1)));
		if (value <= 0 || value >= target || seen.has(value)) continue;
		seen.add(value);
		out.push({ value, reached: hasStart && baseline! >= value });
	}
	return out;
}

// --- Strategy card derivations (shared by the detail view + the dock cards) ----

// The north-star metric: the one tagged role==='primary', else the first (a lone metric is
// primary). Returns the normalized metric + its raw index, or null when there are no metrics.
// Mirrors StrategyDetailView's orderedMetrics primary selection so the card and detail agree.
export function selectPrimaryMetric(
	metrics: readonly unknown[] | null | undefined,
): { metric: StrategyMetric; rawIndex: number } | null {
	const ms = metrics ?? [];
	if (ms.length === 0) return null;
	const idx = Math.max(
		0,
		ms.findIndex((m) => (m as { role?: string } | null)?.role === 'primary'),
	);
	return {
		metric: normalizePlanMetric(ms[idx] as Record<string, unknown>),
		rawIndex: idx,
	};
}

// Every known readiness can be activated — an unknown/absent readiness on a partial
// stored plan still gets no Activate.
export function isActivatableReadiness(
	readiness: string | null | undefined,
): boolean {
	return readiness === 'executable_now' || readiness === 'requires_setup';
}

// --- Step lanes (ADO-1757) ------------------------------------------------
// The lane lives on the STEP so one campaign can hold both kinds of work. A step with no
// `runBy` is Bridgeway's, which is every step generated before the field existed.

export function isOwnerStep(
	step: { runBy?: string } | null | undefined,
): boolean {
	return step?.runBy === 'owner';
}

export function campaignHasOwnerWork(
	campaign: { steps?: { runBy?: string }[] } | null | undefined,
): boolean {
	return (campaign?.steps ?? []).some(isOwnerStep);
}

export function isOwnerRun(
	campaign: { steps?: { runBy?: string }[] } | null | undefined,
): boolean {
	const steps = campaign?.steps ?? [];
	return steps.length > 0 && steps.every(isOwnerStep);
}

// --- Build state ----------------------------------------------------------

/**
 * How far one campaign has got, as the surfaces need to talk about it.
 *
 * `built` and `live` are separate because they are separate facts, and conflating
 * them is the bug this type exists to prevent: materializing builds a Process
 * sitting in `proposed` with nothing running, and the owner's yes is what starts
 * it. A card that says "Activated" about a `materialized` campaign is claiming
 * work began that has not.
 *
 * `processId` rides on exactly the two states that have one, so a caller cannot
 * reach for it before it exists.
 */
export type CampaignBuildState =
	| { kind: 'not_built' }
	| { kind: 'building' }
	| { kind: 'built'; processId: string }
	| { kind: 'live'; processId: string }
	| { kind: 'failed'; message: string };

/**
 * The three fields that decide a campaign's build state.
 *
 * Structural rather than `StrategyCampaignProposal` so the build route's own reply
 * — which carries exactly these — can be read by the same function. That is the
 * point: the state after a click and the state after a push are then the same
 * derivation, not two that can drift.
 */
export type CampaignBuildFacts = {
	// `'superseded'` never appears on a STORED campaign: the route clears the marker before it
	// answers. It reaches here only from a live materialize reply, and it is named so the compiler
	// forces every caller to decide what to do with it.
	status?: StrategyCampaignProposal['status'] | 'superseded' | null;
	process_id?: string | null;
	error?: string | null;
};

/**
 * Read the build state off the campaign itself.
 *
 * ONE derivation, from the record the server writes. There used to be a second,
 * private to the activate control, matching a status string that a rename had
 * already moved — which read a mid-build campaign as never-built and put a fresh
 * button under work already in flight. Two derivations that can disagree is how
 * that happened, so this is the only one.
 */
export function campaignBuildState(
	campaign: CampaignBuildFacts | undefined | null,
): CampaignBuildState {
	if (!campaign) return { kind: 'not_built' };
	const { status, process_id: processId } = campaign;

	if (!status) {
		// A cleared status carrying an error is the failure shape: the backend's
		// marker clear writes `{status: null, error: "…"}` together. The message is a
		// truncated exception, so it is carried for branching only — never printed.
		const error = campaign.error?.trim();
		return error ? { kind: 'failed', message: error } : { kind: 'not_built' };
	}
	if (status === 'materializing') return { kind: 'building' };
	if (status === 'materialized' && processId) {
		return { kind: 'built', processId };
	}
	if (status === 'activated' && processId) return { kind: 'live', processId };
	// A superseded build was thrown away, so there is no content and no Process. `not_built` is
	// the truthful reading, and the control that pressed it explains why before showing it.
	if (status === 'superseded') return { kind: 'not_built' };
	// A terminal status with no `process_id` is an incoherent record — there is
	// nothing to show and nothing to open, so treat it as never built rather than
	// rendering a state whose content cannot be fetched.
	return { kind: 'not_built' };
}

// --- Read guards (tolerate legacy / partial stored plans) -----------------

// String → one online locus (empty → [], not a junk {place:""}); array passes through,
// dropping blank places and defaulting a missing scope to "online". Nullish → [].
export function normalizeWhere(where: unknown): WhereLocus[] {
	if (typeof where === 'string') {
		const place = where.trim();
		return place ? [{ scope: 'online', place, note: '' }] : [];
	}
	if (!Array.isArray(where)) return [];
	const out: WhereLocus[] = [];
	for (const raw of where) {
		if (!raw || typeof raw !== 'object') continue;
		const r = raw as Record<string, unknown>;
		const place = typeof r.place === 'string' ? r.place.trim() : '';
		if (!place) continue;
		const scope: WhereLocus['scope'] =
			r.scope === 'offline' ? 'offline' : 'online';
		const note = typeof r.note === 'string' ? r.note : '';
		out.push({ scope, place, note });
	}
	return out;
}

// Reconstruct a canonical StrategyMetric from a loose/legacy stored row: a live LLM run
// could persist metric==={} with fields flattened to the top level under mixed aliases.
// Heals already-persisted rows so the detail view renders values instead of white-screening.
export function normalizePlanMetric(
	m: Record<string, unknown>,
): StrategyMetric {
	const inner = (
		m.metric && typeof m.metric === 'object' ? m.metric : {}
	) as Record<string, unknown>;
	const pick = (...keys: string[]): unknown => {
		for (const k of keys) {
			if (inner[k] != null) return inner[k];
			if (m[k] != null) return m[k];
		}
		return undefined;
	};
	const num = (v: unknown): number | undefined =>
		typeof v === 'number' && Number.isFinite(v) ? v : undefined;

	const basisRaw = pick('basis', 'targetBasis');
	const basis = (
		basisRaw && typeof basisRaw === 'object'
			? basisRaw
			: { source: 'assumption' }
	) as MetricBasis;

	const existingKey = (inner.tracking as Record<string, unknown> | undefined)
		?.key;
	const instructions =
		(pick('instructions', 'manualInstructions', 'trackingInstructions') as
			| string
			| undefined) ?? '';
	const tracking: MetricTracking =
		typeof existingKey === 'string'
			? { key: existingKey }
			: { manual: true, instructions };

	return {
		id: String(m.id ?? ''),
		role: m.role as 'primary' | 'supporting' | undefined,
		purpose: (m.purpose as string) ?? '',
		metric: {
			name: (pick('name', 'label', 'title') as string) ?? '',
			target: num(pick('target', 'targetValue', 'value', 'goal')) ?? 0,
			unit: (pick('unit') as string) ?? '',
			basis,
			baseline: num(pick('baseline')),
			outcome: pick('outcome', 'outcomeKey') as string | undefined,
			tracking,
		},
		funnelStage: (m.funnelStage ??
			m.funnel_stage ??
			m.stage ??
			'win_attention') as FunnelStage,
		provenance: Array.isArray(m.provenance)
			? (m.provenance as StrategyMetric['provenance'])
			: [],
	};
}

// --- Label maps (never surface a raw enum / internal noun) ----------------

export function humanize(raw: string | null | undefined): string {
	// Null-safe: a loose LLM plan can persist a type-less artifact/action; never white-screen
	// on a stray undefined reaching a formatter.
	if (!raw) return '';
	return raw
		.replace(/[_:-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, (c) => c.toUpperCase());
}

const FUNNEL_LABEL: Record<string, string> = {
	win_attention: 'Win attention',
	generate_leads: 'Generate leads',
	boost_revenue: 'Boost revenue',
	retain_customers: 'Retain customers',
	grow_referrals: 'Grow referrals',
};
export const funnelLabel = (s: string): string =>
	FUNNEL_LABEL[s] ?? humanize(s);

const FUNNEL_VARIANT: Record<string, string> = {
	win_attention: 'purple',
	generate_leads: 'orange',
	boost_revenue: 'green',
	retain_customers: 'brown',
	grow_referrals: 'magenta',
};
export const funnelVariant = (s: string): string =>
	FUNNEL_VARIANT[s] ?? 'neutral';

// DESIGN.md forbids "competitor" in user-facing strings; label bases in plain
// language and never surface a raw ref id.
const BASIS_LABEL: Record<string, string> = {
	profile: 'your profile',
	competitor: 'a similar business',
	'pattern-evidence': 'interview evidence',
	research: 'research',
	assumption: 'an estimate',
	owner_goal: 'your stated goal',
};
export const basisLabel = (s: string): string => BASIS_LABEL[s] ?? humanize(s);

// Map a channel prerequisite to the plain-language action it unlocks.
const PREREQ_ACTION: Record<string, string> = {
	igGraphConnected: 'Connect Instagram',
	expressConnected: 'Connect Adobe Express',
	contactsAvailable: 'Import contacts',
	squareConnected: 'Connect Square',
};
// A known connection key becomes its short action label. Anything else is PROSE the model wrote
// — a whole sentence — and `Set up ${humanize(prose)}` Title-Cased the entire sentence, which is
// how "Set up Connect Adobe Express So Beacon Can Publish The Approved Instagram Posts." reached
// the screen. Prose has no place in a one-line status pill, so it returns null and the caller
// renders it as text instead.
export const prereqAction = (p: string): string | null =>
	PREREQ_ACTION[p] ?? null;

// Prerequisites that are prose, in the model's own words, for rendering as body text.
export const prereqProse = (prereqs: string[] | undefined): string[] =>
	(prereqs ?? []).filter((p) => p && !PREREQ_ACTION[p]);

// The short labels only, for the status pill.
export const prereqActions = (prereqs: string[] | undefined): string[] =>
	(prereqs ?? []).map(prereqAction).filter((a): a is string => a !== null);

// --- Term-of-art glossing ---
// Wrap each model-emitted {term, plain} definition at its FIRST plan-wide occurrence in a
// tooltip. Pure presentation over the model's pairs; GlossedText (StrategyBits) renders them.

export type GlossSegment =
	| { kind: 'plain'; text: string }
	| { kind: 'term'; text: string; term: string; plain: string };

export function normalizeTerm(term: string): string {
	return term
		.trim()
		.toLowerCase()
		.replace(/(?:'s|es|s)$/, '');
}

function termRegex(term: string): RegExp | null {
	const t = term.trim();
	if (!t) return null;
	const escaped = t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const startsWord = /\w/.test(t[0]!);
	const endsWord = /\w/.test(t[t.length - 1]!);
	const left = startsWord ? '(?<!\\w)' : '';
	const right = endsWord ? "(?:'s|es|s)?(?!\\w)" : '';
	return new RegExp(`${left}${escaped}${right}`, 'i');
}

// Split `text` into ordered segments, wrapping every occurrence of any defined term.
// Invariant: segments joined === input. Longest term first so a short match can't pre-empt a longer.
export function segmentByTerms(
	text: string,
	definitions: StrategyDefinition[] | undefined,
): GlossSegment[] {
	if (!text) return [];
	const defs = (definitions ?? []).filter((d) => d.term.trim());
	if (!defs.length) return [{ kind: 'plain', text }];
	const ordered = [...defs].sort(
		(a, b) => b.term.trim().length - a.term.trim().length,
	);
	const segments: GlossSegment[] = [];
	let cursor = 0;
	while (cursor < text.length) {
		let best: {
			index: number;
			length: number;
			def: StrategyDefinition;
		} | null = null;
		for (const d of ordered) {
			const re = termRegex(d.term);
			if (!re) continue;
			const m = re.exec(text.slice(cursor));
			if (m && m.index >= 0) {
				const abs = cursor + m.index;
				if (
					!best ||
					abs < best.index ||
					(abs === best.index && m[0].length > best.length)
				) {
					best = { index: abs, length: m[0].length, def: d };
				}
			}
		}
		if (!best) {
			segments.push({ kind: 'plain', text: text.slice(cursor) });
			break;
		}
		if (best.index > cursor)
			segments.push({ kind: 'plain', text: text.slice(cursor, best.index) });
		segments.push({
			kind: 'term',
			text: text.slice(best.index, best.index + best.length),
			term: best.def.term,
			plain: best.def.plain,
		});
		cursor = best.index + best.length;
	}
	return segments;
}

// The canonical prose corpus in FIXED order (mirrors Python rendered_owner_facing_prose):
// determines each term's single first-occurrence gloss site.
function renderedOwnerFacingProse(
	strategy: StrategicDirection | undefined,
	campaigns: StrategyCampaignProposal[] | undefined,
): { where: string; text: string }[] {
	const out: { where: string; text: string }[] = [];
	const push = (where: string, text: string | undefined | null) => {
		if (text && text.trim()) out.push({ where, text });
	};
	push('statement', strategy?.statement);
	(campaigns ?? []).forEach((c, ci) => {
		push(`campaign[${ci}].aim`, c.aim);
		(c.steps ?? []).forEach((s, si) =>
			push(`campaign[${ci}].step[${si}].description`, s.description),
		);
		push(`campaign[${ci}].contributes[0].how`, (c.contributes ?? [])[0]?.how);
		// Keyed on owner work, not readiness: a mixed campaign derives to executable_now, and
		// reading readiness here would drop the owner's report-back question from the gloss
		// corpus. Mirrors terms.py.
		if (campaignHasOwnerWork(c))
			push(`campaign[${ci}].reportBack`, c.reportBack);
	});
	(strategy?.metrics ?? []).forEach((m, mi) =>
		push(`metric[${mi}].purpose`, m.purpose),
	);
	return out;
}

// term (normalized) → the single `where` field where it gets wrapped (first plan-wide
// occurrence). A term is glossed exactly ONCE across the whole plan.
export function canonicalGlossSites(
	strategy: StrategicDirection | undefined,
	campaigns: StrategyCampaignProposal[] | undefined,
): Map<string, string> {
	const sites = new Map<string, string>();
	const defs = strategy?.definitions ?? [];
	if (!defs.length) return sites;
	for (const field of renderedOwnerFacingProse(strategy, campaigns)) {
		for (const seg of segmentByTerms(field.text, defs)) {
			if (seg.kind !== 'term') continue;
			const key = normalizeTerm(seg.term);
			if (!sites.has(key)) sites.set(key, field.where);
		}
	}
	return sites;
}

// Segment ONE field, wrapping only terms whose canonical site is THIS field, and only
// their first occurrence in the field (later repeats stay plain — one tab-stop, not many).
export function glossField(
	where: string,
	text: string,
	definitions: StrategyDefinition[] | undefined,
	sites: Map<string, string>,
): GlossSegment[] {
	if (!text) return [];
	const local = (definitions ?? []).filter(
		(d) => sites.get(normalizeTerm(d.term)) === where,
	);
	if (!local.length) return [{ kind: 'plain', text }];
	const seen = new Set<string>();
	return segmentByTerms(text, local).map((seg): GlossSegment => {
		if (seg.kind !== 'term') return seg;
		const key = normalizeTerm(seg.term);
		if (seen.has(key)) return { kind: 'plain', text: seg.text };
		seen.add(key);
		return seg;
	});
}
